// Mock data for teachers - SAT V4 Zone
import type { Teacher } from '../../types';

const departments = [
  'English',
  'Mathematics',
  'Science',
  'Humanities',
  'Languages',
  'Arts',
  'PE',
  'Technology',
  'Computing',
];
const subjects = [
  'Eng',
  'Ma',
  'Sci',
  'Phy',
  'Che',
  'Bio',
  'His',
  'Geo',
  'Fre',
  'Spa',
  'Ger',
  'Art',
  'Mus',
  'Dra',
  'Pe',
  'Dt',
  'Ict',
  'Rs',
];
const possibleRoles: (
  | 'Head of department'
  | 'Head of Year'
  | 'SLT'
  | 'SEND'
  | 'Safeguarding'
  | 'Behavior'
  | 'Attendance'
)[] = ['Head of department', 'Head of Year', 'SLT', 'SEND', 'Safeguarding', 'Behavior', 'Attendance'];

function generateAvailability() {
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  const periods = ['p1', 'p2', 'p3', 'p4', 'p5', 'p6'];
  const availability: Record<string, Record<string, 'available' | 'unavailable' | 'preferred'>> = {};

  for (const day of days) {
    availability[day] = {};
    for (const period of periods) {
      const rand = Math.random();
      availability[day][period] = rand > 0.9 ? 'unavailable' : rand > 0.7 ? 'preferred' : 'available';
    }
  }
  return availability;
}

const firstNames = [
  'James',
  'Sarah',
  'Michael',
  'Emma',
  'David',
  'Sophie',
  'Robert',
  'Emily',
  'William',
  'Olivia',
  'Richard',
  'Charlotte',
  'Thomas',
  'Amelia',
  'Christopher',
  'Mia',
  'Daniel',
  'Isabella',
  'Matthew',
  'Jessica',
  'Andrew',
  'Grace',
  'Joseph',
  'Lily',
  'Charles',
  'Hannah',
  'Steven',
  'Ella',
  'Mark',
  'Victoria',
  'Paul',
  'Ruby',
  'George',
  'Alice',
  'Edward',
  'Sophia',
  'Brian',
  'Chloe',
  'Kevin',
  'Eva',
  'Jonathan',
  'Zoe',
  'Peter',
  'Lucy',
  'Anthony',
  'Daisy',
  'Benjamin',
  'Poppy',
  'Nicholas',
  'Freya',
];
const lastNames = [
  'Smith',
  'Johnson',
  'Williams',
  'Brown',
  'Jones',
  'Garcia',
  'Miller',
  'Davis',
  'Rodriguez',
  'Martinez',
  'Wilson',
  'Anderson',
  'Taylor',
  'Thomas',
  'Hernandez',
  'Moore',
  'Martin',
  'Jackson',
  'Thompson',
  'White',
  'Lopez',
  'Lee',
  'Gonzalez',
  'Harris',
  'Clark',
  'Lewis',
  'Robinson',
  'Walker',
  'Perez',
  'Hall',
  'Young',
  'Allen',
  'Sanchez',
  'Wright',
  'King',
  'Scott',
  'Green',
  'Baker',
  'Adams',
  'Nelson',
  'Hill',
  'Ramirez',
  'Campbell',
  'Mitchell',
  'Roberts',
  'Carter',
  'Phillips',
  'Evans',
  'Turner',
  'Torres',
];
const titles: ('Mr' | 'Mrs' | 'Ms' | 'Miss' | 'Dr')[] = ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr'];

const preferenceTypes = ['room', 'time', 'day', 'consecutive', 'break'];
const preferenceDescriptions: Record<string, string[]> = {
  room: [
    'Prefers ground floor rooms',
    'Needs wheelchair accessible room',
    'Prefers room with projector',
    'Prefers quiet room away from gym',
  ],
  time: ['Prefers morning lessons', 'Prefers afternoon lessons', 'Avoid first period', 'Avoid last period'],
  day: [
    'No lessons on Monday',
    'No lessons on Friday afternoon',
    'Prefers not to work Wednesday PM',
    'Light schedule on Thursday',
  ],
  consecutive: ['Maximum 3 consecutive lessons', 'No back-to-back lessons', 'Prefers double periods together'],
  break: ['Needs break for lunch duty', 'Requires break at 10:30', 'Morning break supervision'],
};

export const mockTeachers: Teacher[] = Array.from({ length: 75 }, (_, i) => {
  const firstName = firstNames[i % firstNames.length];
  const lastName = lastNames[i % lastNames.length];
  const title = titles[Math.floor(Math.random() * titles.length)];
  const dept = departments[Math.floor(Math.random() * departments.length)];
  const teacherSubjects = subjects.slice(0, Math.floor(Math.random() * 3) + 1);
  const teacherId = `teacher-${String(i + 1).padStart(3, '0')}`;
  const teacherName = `${title} ${firstName} ${lastName}`;

  // Generate 0-3 roles per teacher (some teachers have no additional roles)
  const numRoles = Math.floor(Math.random() * 4);
  const teacherRoles: typeof possibleRoles = [];
  if (numRoles > 0) {
    const shuffled = [...possibleRoles].sort(() => Math.random() - 0.5);
    teacherRoles.push(...shuffled.slice(0, numRoles));
  }

  const maxPeriods = 25;
  const currentAssigned = Math.floor(Math.random() * maxPeriods);
  // For bi-weekly, split periods with slight variation between weeks
  const currentWeekA = Math.floor(currentAssigned / 2) + Math.floor(Math.random() * 2);
  const currentWeekB = currentAssigned - currentWeekA;

  // Generate 1-3 preferences per teacher with varied types and statuses
  const numPrefs = Math.floor(Math.random() * 3) + 1;
  const teacherPrefs = Array.from({ length: numPrefs }, (_, j) => {
    const prefType = preferenceTypes[Math.floor(Math.random() * preferenceTypes.length)];
    const descriptions = preferenceDescriptions[prefType];
    const description = descriptions[Math.floor(Math.random() * descriptions.length)];
    const priority = (Math.floor(Math.random() * 3) + 1) as 1 | 2 | 3;
    const isApplied = Math.random() > 0.3; // 70% applied, 30% not applied

    return {
      id: `pref-${i}-${j + 1}`,
      teacherId,
      teacherName,
      department: dept,
      type: prefType,
      description,
      priority,
      isApplied,
    };
  });

  return {
    id: teacherId,
    title,
    firstName,
    lastName,
    email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@school.edu`,
    phone: `0${Math.floor(1000000000 + Math.random() * 9000000000)}`,
    employeeId: `EMP${String(i + 1).padStart(4, '0')}`,
    roles: teacherRoles,
    department: dept,
    subjects: teacherSubjects,
    maxPeriodsPerWeek: maxPeriods,
    maxPeriodsWeekA: maxPeriods,
    maxPeriodsWeekB: maxPeriods,
    currentPeriodsAssigned: currentAssigned,
    currentPeriodsWeekA: currentWeekA,
    currentPeriodsWeekB: currentWeekB,
    availability: generateAvailability(),
    preferences: teacherPrefs,
    hireDate: `20${10 + Math.floor(Math.random() * 14)}-${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}-01`,
    status: Math.random() > 0.1 ? 'active' : Math.random() > 0.5 ? 'on_leave' : 'inactive',
  };
});

export default mockTeachers;
